import React, { useState } from "react";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { Background } from "../../BuildingBlocks/Background";
import { BackgroundColour } from "../../BuildingBlocks/BackgroundColour";
import { Sticky } from "../../BuildingBlocks/Sticky";
import { Border } from "../../BuildingBlocks/Border";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import { WeightBold } from "../../WeightBold/WeightBold";
import { CRMButtonPrimary } from "../../CRMButtonPrimary/CRMButtonPrimary";
import {
    CRMCaseDocumentTypeSelect,
    TCaseDocumentTypeTileInfo,
    TCaseDocumentTypeSelection,
} from "../CRMCaseDocumentTypeSelect/CRMCaseDocumentTypeSelect";
import {
    CRMFileAttachmentAssignmentRow,
    TFileAttachmentAssignmentRowSelection,
} from "../CRMFileAttachmentAssignmentRow/CRMFileAttachmentAssignmentRow";

export type TFileAttachmentToAssign = {
    download_token: string;
    name: string;
};

export type TAssignmentCommit = {
    assignments: Array<{
        legal_file_download_tokens: Array<string>;
        target:
            | { tag: "existing_tile"; cases_document_id: string }
            | { tag: "new_tile"; type: string; additional_name: string };
    }>;
};

type TCRMFileAttachmentsAssignmentViewProps = {
    attachments: ReadonlyArray<TFileAttachmentToAssign>;
    existingTiles: ReadonlyArray<TCaseDocumentTypeTileInfo>;
    onCommit: (payload: TAssignmentCommit) => void;
    onCancel: () => void;
};

type TRowState = {
    download_token: string;
    name: string;
    selected: boolean;
    assignment: TFileAttachmentAssignmentRowSelection;
};

const toAssignment = (a: TCaseDocumentTypeSelection): TFileAttachmentAssignmentRowSelection => a;

export const CRMFileAttachmentsAssignmentView = (props: React.PropsWithChildren<TCRMFileAttachmentsAssignmentViewProps>): JSX.Element => {
    const [rows, setRows] = useState<Array<TRowState>>(
        props.attachments.map((a) => ({
            download_token: a.download_token,
            name: a.name,
            selected: false,
            assignment: { tag: "unassigned" },
        }))
    );
    const [pickerMode, setPickerMode] = useState<null | { tag: "single"; index: number } | { tag: "bulk" }>(null);

    const updateRow = (index: number, assignment: TFileAttachmentAssignmentRowSelection) =>
        setRows((prev) => prev.map((r, i) => i === index ? { ...r, assignment, selected: false } : r));

    const applyToSelected = (assignment: TFileAttachmentAssignmentRowSelection) =>
        setRows((prev) => prev.map((r) => r.selected ? { ...r, assignment, selected: false } : r));

    const toggleSelected = (index: number) =>
        setRows((prev) => prev.map((r, i) => i === index ? { ...r, selected: !r.selected } : r));

    const selectedCount = rows.filter((r) => r.selected).length;
    const assignedCount = rows.filter((r) => r.assignment.tag !== "unassigned").length;
    const canCommit = assignedCount > 0;

    const onPick = (selection: TCaseDocumentTypeSelection) => {
        if (pickerMode === null) return;
        if (pickerMode.tag === "single") {
            updateRow(pickerMode.index, toAssignment(selection));
        } else {
            applyToSelected(toAssignment(selection));
        }
        setPickerMode(null);
    };

    const buildCommit = (): TAssignmentCommit => {
        const groups = new Map<string, { target: TAssignmentCommit["assignments"][number]["target"]; tokens: Array<string> }>();
        for (const r of rows) {
            if (r.assignment.tag === "unassigned") continue;
            const key = r.assignment.tag === "existing_tile"
                ? `existing:${r.assignment.cases_document_id}`
                : `new:${r.assignment.type}`;
            const entry = groups.get(key);
            if (entry) {
                entry.tokens.push(r.download_token);
            } else {
                const target = r.assignment.tag === "existing_tile"
                    ? { tag: "existing_tile" as const, cases_document_id: r.assignment.cases_document_id }
                    : { tag: "new_tile" as const, type: r.assignment.type, additional_name: "" };
                groups.set(key, { target, tokens: [r.download_token] });
            }
        }
        return {
            assignments: Array.from(groups.values()).map((g) => ({
                legal_file_download_tokens: g.tokens,
                target: g.target,
            })),
        };
    };

    if (pickerMode !== null) {
        const pickerTitle = pickerMode.tag === "bulk"
            ? `Pick document type for ${selectedCount} attachments`
            : "Pick document type";

        return (
            <Background color={CRMColors.NEUTRAL_PAPER}>
                <Sticky top="0px" zIndex={2}>
                    <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
                        <Border type="bottom" definition={`1px solid ${CRMColors.NEUTRAL_10}`}>
                            <Background padding={CRMSpacing.MEDIUM}>
                                <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="center">
                                    <CRMIcon
                                        iconName="arrow-left"
                                        colour="neutral-ink"
                                        onClick={() => setPickerMode(null)}
                                    />
                                    <CRMParagraph fontSize={CRMFontSizes.MED}>
                                        <WeightBold>{pickerTitle}</WeightBold>
                                    </CRMParagraph>
                                </SpacingRow>
                            </Background>
                        </Border>
                    </BackgroundColour>
                </Sticky>

                <CRMCaseDocumentTypeSelect
                    existingTiles={props.existingTiles}
                    onSelect={onPick}
                />
            </Background>
        );
    }

    const commitLabel = assignedCount === 1
        ? "File 1 attachment to doc store"
        : `File ${assignedCount} attachments to doc store`;

    return (
        <Background color={CRMColors.NEUTRAL_PAPER}>
            {/* STICKY TOP ACTION BAR */}
            <Sticky top="0px" zIndex={2}>
                <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
                    <Border type="bottom" definition={`1px solid ${CRMColors.NEUTRAL_10}`}>
                        <Background padding={CRMSpacing.MEDIUM}>
                            <SpacingRow spacing={CRMSpacing.MEDIUM} justifyContent="space-between" alignItems="center">
                                <CRMIcon
                                    iconName="arrow-left"
                                    colour="neutral-ink"
                                    onClick={props.onCancel}
                                />
                                <CRMButtonPrimary
                                    label={commitLabel}
                                    icon="attach"
                                    disabled={!canCommit}
                                    onClick={() => canCommit && props.onCommit(buildCommit())}
                                />
                            </SpacingRow>
                        </Background>
                    </Border>
                </BackgroundColour>
            </Sticky>

            {/* BULK ACTION BAR */}
            {selectedCount > 0 && (
                <Sticky top="64px" zIndex={1}>
                    <BackgroundColour colour={CRMColors.PRIMARY_13}>
                        <Border type="bottom" definition={`1px solid ${CRMColors.PRIMARY_10}`}>
                            <Background padding={CRMSpacing.MEDIUM}>
                                <SpacingRow spacing={CRMSpacing.MEDIUM} justifyContent="space-between" alignItems="center">
                                    <CRMParagraph fontSize={CRMFontSizes.SMALL}>
                                        <WeightBold>{selectedCount} selected</WeightBold>
                                    </CRMParagraph>
                                    <CRMButtonPrimary
                                        label={`Apply type to ${selectedCount} selected`}
                                        onClick={() => setPickerMode({ tag: "bulk" })}
                                    />
                                </SpacingRow>
                            </Background>
                        </Border>
                    </BackgroundColour>
                </Sticky>
            )}

            {/* ROWS */}
            <Background padding={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    {rows.map((row, index) => (
                        <CRMFileAttachmentAssignmentRow
                            key={row.download_token}
                            download_token={row.download_token}
                            name={row.name}
                            selected={row.selected}
                            assignment={row.assignment}
                            onToggleSelected={() => toggleSelected(index)}
                            onOpenTypePicker={() => setPickerMode({ tag: "single", index })}
                        />
                    ))}
                </SpacingColumn>
            </Background>
        </Background>
    );
};
